import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  addUserKey,
  createInvite,
  deleteUser,
  getInviteStatus,
  getUser,
  inviteUrl,
  removeUserKey,
  resetPassword,
  setEnabled,
  updateUser,
  type AuthMethod,
  type Folder,
  type InviteStatus,
  type Profile,
  type User,
} from '../api';
import { FolderEditor } from '../components/Folders';
import { AddKeyForm, KeyList } from '../components/Keys';
import { OneTimeSecret } from '../components/OneTimeSecret';
import { RoleNote, useCan } from '../staff';

export function AdminUserPage() {
  const { username = '' } = useParams();
  const navigate = useNavigate();
  // What this staff member may change here. The API checks all of these again --
  // hiding a control only spares somebody a 403 they could do nothing about.
  const mayEditRoster = useCan('roster:write');
  const mayEditCredentials = useCan('credentials:write');
  const mayListFiles = useCan('files:list');
  const [user, setUser] = useState<User>();
  const [error, setError] = useState<string>();
  const [newPassword, setNewPassword] = useState<string>();

  const reload = useCallback(async () => {
    setError(undefined);
    try {
      setUser(await getUser(username));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [username]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Every mutation reloads rather than patching local state. The API is the thing
  // that decides what a profile ends up looking like -- it normalizes and
  // validates -- so echoing an optimistic guess back at the admin would sometimes
  // be a lie.
  const run = async (action: () => Promise<unknown>) => {
    setError(undefined);
    try {
      await action();
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (error && !user) return <p className="error">{error}</p>;
  if (!user) return <p>Loading…</p>;

  const usesKey = user.authMethod !== 'password';
  const usesPassword = user.authMethod !== 'ssh-key';

  return (
    <section>
      <header className="page-header">
        <div>
          <h1>{user.username}</h1>
          <p className="hint">
            Home directory <code>{user.homeDirectory}/</code> ·{' '}
            <Link to={`/access?who=${encodeURIComponent(user.username)}`}>what they can reach</Link>
            {mayListFiles && (
              <>
                {' · '}
                <Link to={`/files/${user.homeDirectory}`}>browse their files</Link>
              </>
            )}
          </p>
        </div>
        <Link className="link" to="/users">
          Back to users
        </Link>
      </header>

      {error && <p className="error">{error}</p>}

      {!user.account.exists && (
        <p className="warn card">
          This profile has no Cognito account, so {user.username} cannot sign in or use SFTP at all.
          That happens when a create failed part-way. Delete the user here and create them again.
        </p>
      )}

      {mayEditRoster ? (
        <Details user={user} onSave={(patch) => run(() => updateUser(user.username, patch))} />
      ) : (
        <ReadOnlyDetails user={user} />
      )}

      {usesKey && (
        <div className="card wide">
          <h2>Public keys</h2>
          <KeyList
            keys={user.publicKeys ?? []}
            onRemove={
              mayEditCredentials
                ? (keyId) => void run(() => removeUserKey(user.username, keyId))
                : undefined
            }
          />
          {mayEditCredentials ? (
            <AddKeyForm
              defaultComment={user.username}
              generateNote={`The private key downloads to this computer, not to ${user.username}. Send it over a channel you trust and delete your copy afterwards — or have them generate their own from their account page instead.`}
              onAdd={async (body) => {
                await addUserKey(user.username, body);
                await reload();
              }}
            />
          ) : (
            <RoleNote what="add or remove public keys" />
          )}
        </div>
      )}

      {usesPassword && !mayEditCredentials && (
        <div className="card wide">
          <h2>Password</h2>
          <RoleNote what="reset a password" />
        </div>
      )}

      {usesPassword && mayEditCredentials && (
        <div className="card wide">
          <h2>Password</h2>
          {newPassword ? (
            <OneTimeSecret
              label="New password"
              value={newPassword}
              note={`This replaces ${user.username}'s previous password immediately. It is shown once and is not recoverable.`}
            />
          ) : (
            <>
              <p className="hint">
                Resets the password to a newly generated one, shown once. There is no way to read
                the current password — nobody has it but the user.
              </p>
              <button
                type="button"
                onClick={() =>
                  void run(async () => {
                    const result = await resetPassword(user.username, { generatePassword: true });
                    setNewPassword(result.password);
                  })
                }
              >
                Generate a new password
              </button>
            </>
          )}
        </div>
      )}

      <div className="card wide">
        <h2>Invitation</h2>
        {mayEditCredentials ? (
          <Invitation user={user} />
        ) : (
          <RoleNote what="send an invitation link" />
        )}
      </div>

      {/* Nothing to show a viewer: both controls in here are changes, and an empty
          danger zone would only invite a second look for the button. */}
      {(mayEditCredentials || mayEditRoster) && (
        <div className="card wide danger-zone">
          <h2>Access</h2>
          {mayEditCredentials && (
            <>
              <p className="hint">
                Disabling blocks both SFTP and portal sign-in immediately and leaves everything else
                intact. It is the right move for a suspected compromise or an offboarding you may
                need to undo.
              </p>
              <button
                type="button"
                onClick={() => void run(() => setEnabled(user.username, !user.account.enabled))}
                disabled={!user.account.exists}
              >
                {user.account.enabled ? 'Disable this account' : 'Enable this account'}
              </button>
            </>
          )}

          {mayEditCredentials && mayEditRoster && <hr />}

          {mayEditRoster && (
            <DeleteUser
              username={user.username}
              onDelete={async () => {
                await deleteUser(user.username);
                navigate('/users');
              }}
            />
          )}
        </div>
      )}
    </section>
  );
}

// Issuing the one-time link a partner uses to set their own credentials.
//
// This is the path that avoids a human ever handling someone else's credential:
// the alternative on this page is generating a keypair or a password here and
// sending it, which puts a working secret in a chat log or a mailbox. The link
// grants nothing but the right to set credentials once, and expires.
//
// Loads its own status rather than taking it from the page's reload(), because
// the invite lives in a different table and a viewer may not read it at all --
// the route behind it requires credentials:write.
function Invitation({ user }: { user: User }) {
  const [status, setStatus] = useState<InviteStatus>();
  const [link, setLink] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const reload = useCallback(async () => {
    try {
      setStatus(await getInviteStatus(user.username));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [user.username]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // The link is built here from the token, and the token is in this response
  // only -- the API stores a hash and can never show it again.
  const issue = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const invite = await createInvite(user.username);
      setLink(inviteUrl(invite));
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (link) {
    return (
      <OneTimeSecret
        label="Invitation link"
        value={link}
        note={`Send this to ${user.username} over a channel you trust. It is shown once, works once, and expires in 72 hours. Anyone who has it can register a key on this account, so treat it like a password.`}
      />
    );
  }

  return (
    <>
      <p className="hint">
        Creates a one-time link that lets {user.username} set their own password or register their
        own public key. Nothing you send them is a working credential, and nobody here ever sees
        what they chose.
      </p>

      {/* Stated because it is the common case and it looks like a bug otherwise:
          a key-only account has a password nobody knows, by design, so there is
          no way for them to sign in and use the self-service key page. */}
      {user.authMethod === 'ssh-key' && (
        <p className="hint">
          This account signs in with a key only, so {user.username} cannot sign in to the portal.
          An invitation is the only way for them to upload their own key.
        </p>
      )}

      {status?.usedAt && (
        <p>
          Accepted {new Date(status.usedAt).toLocaleString()}
          {status.usedFromIp && status.usedFromIp !== 'unknown' && ` from ${status.usedFromIp}`}.
        </p>
      )}
      {status?.outstanding && (
        <p className="warn">
          An invitation issued {new Date(status.createdAt ?? '').toLocaleString()} by{' '}
          {status.createdBy} is still unused, and expires{' '}
          {new Date(status.expiresAt ?? '').toLocaleString()}. That link cannot be shown again —
          issuing a new one below replaces it and stops the old one working.
        </p>
      )}
      {status?.expired && !status.usedAt && (
        <p className="hint">The last invitation expired without being used.</p>
      )}

      {!user.account.enabled && (
        <p className="warn">
          This account is disabled, so an invitation would not let them in. Enable it first.
        </p>
      )}

      {error && <p className="error">{error}</p>}
      <button type="button" onClick={() => void issue()} disabled={busy || !user.account.enabled}>
        {busy ? 'Creating…' : status?.outstanding ? 'Replace with a new link' : 'Create an invitation link'}
      </button>
    </>
  );
}

// What the editable form shows, without the means to change any of it. A disabled
// copy of the form would have been less code, but greyed-out inputs are hard to
// read and imply the values are unset rather than unchangeable -- and this is the
// screen somebody is reading precisely because they want to know what the values
// are.
function ReadOnlyDetails({ user }: { user: User }) {
  return (
    <div className="card wide">
      <h2>Details</h2>
      <dl>
        <dt>How they sign in</dt>
        <dd>{AUTH_LABELS[user.authMethod]}</dd>
        <dt>Email</dt>
        <dd>{user.email ?? <span className="hint">none recorded</span>}</dd>
        <dt>Allowed source IPs</dt>
        <dd>
          {user.allowedSourceIps?.length ? (
            user.allowedSourceIps.join(', ')
          ) : (
            <span className="hint">any address</span>
          )}
        </dd>
        <dt>Note</dt>
        <dd>{user.comment ?? <span className="hint">none</span>}</dd>
      </dl>

      <h3>Folders</h3>
      {user.folders.length === 0 ? (
        <p className="hint">None — this user can sign in but will see nothing.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>They see</th>
              <th>Access</th>
              <th>Points at</th>
              <th>Upload email</th>
            </tr>
          </thead>
          <tbody>
            {user.folders.map((folder) => (
              <tr key={folder.path}>
                <td>
                  <code>{folder.path}</code>
                </td>
                <td>{folder.permission ?? 'read-write'}</td>
                <td>
                  {folder.sharedPath ? (
                    <>
                      <code className="wrap">{folder.sharedPath}</code>{' '}
                      <span className="badge warn">another company</span>
                    </>
                  ) : (
                    <span className="hint">their own home directory</span>
                  )}
                </td>
                <td>{folder.notifyOnUpload ? 'yes' : <span className="hint">no</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <RoleNote what="change folders or details" />
    </div>
  );
}

const AUTH_LABELS: Record<AuthMethod, string> = {
  'ssh-key': 'SSH key only',
  password: 'Password only',
  'ssh-key-or-password': 'SSH key or password',
};

function Details({
  user,
  onSave,
}: {
  user: User;
  onSave: (patch: Partial<Profile>) => Promise<void>;
}) {
  const [authMethod, setAuthMethod] = useState<AuthMethod>(user.authMethod);
  const [folders, setFolders] = useState<Folder[]>(user.folders);
  const [comment, setComment] = useState(user.comment ?? '');
  const [email, setEmail] = useState(user.email ?? '');
  const [ipsText, setIpsText] = useState((user.allowedSourceIps ?? []).join(', '));
  const [busy, setBusy] = useState(false);

  return (
    <form
      className="card wide"
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        const ips = ipsText
          .split(/[,\s]+/)
          .map((entry) => entry.trim())
          .filter((entry) => entry !== '');
        void onSave({
          authMethod,
          folders: folders.map((folder) => ({
            ...folder,
            path: folder.path.trim(),
            comment: folder.comment?.trim() || undefined,
          })),
          comment: comment.trim() || undefined,
          email: email.trim() || undefined,
          // Omitted rather than empty, which is what the API requires for "any
          // source IP". Sending [] is rejected.
          allowedSourceIps: ips.length > 0 ? ips : undefined,
        }).finally(() => setBusy(false));
      }}
    >
      <h2>Details</h2>
      <div className="grid">
        <label>
          How they sign in
          <select value={authMethod} onChange={(e) => setAuthMethod(e.target.value as AuthMethod)}>
            <option value="ssh-key">SSH key only</option>
            <option value="password">Password only</option>
            <option value="ssh-key-or-password">SSH key or password</option>
          </select>
          {authMethod === 'password' && (user.publicKeys?.length ?? 0) > 0 && (
            <span className="warn">
              This user still has {user.publicKeys?.length} public key(s). Revoke them before
              switching to password-only — saving will otherwise be refused, because keys left on a
              password-only user look active but are ignored.
            </span>
          )}
        </label>
        <label>
          Email
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
      </div>

      <fieldset>
        <legend>Folders</legend>
        <FolderEditor folders={folders} onChange={setFolders} />
        <p className="hint">
          Removing a folder here removes their access to it. The files stay in the bucket — nothing
          on this page deletes partner data.
        </p>
      </fieldset>

      <div className="grid">
        <label>
          Allowed source IPs
          <input
            value={ipsText}
            onChange={(e) => setIpsText(e.target.value)}
            placeholder="empty means any address"
            spellCheck={false}
          />
        </label>
        <label>
          Note
          <input value={comment} onChange={(e) => setComment(e.target.value)} />
        </label>
      </div>

      <button type="submit" disabled={busy}>
        {busy ? 'Saving…' : 'Save changes'}
      </button>
    </form>
  );
}

// Typing the name, rather than a yes/no dialog. Deletion removes the sign-in and
// the profile for good, and the confirmation should cost about as much attention
// as the action does.
function DeleteUser({ username, onDelete }: { username: string; onDelete: () => Promise<void> }) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  return (
    <div>
      <h3>Delete {username}</h3>
      <p className="hint">
        Removes their account and profile. Their files are left in the bucket, because an
        offboarding is exactly when someone still needs what was delivered — deleting those is a
        separate, deliberate job.
      </p>
      <label>
        Type <code>{username}</code> to confirm
        <input value={typed} onChange={(e) => setTyped(e.target.value)} spellCheck={false} />
      </label>
      {error && <p className="error">{error}</p>}
      <button
        type="button"
        className="danger"
        disabled={busy || typed !== username}
        onClick={() => {
          setBusy(true);
          setError(undefined);
          onDelete()
            .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)))
            .finally(() => setBusy(false));
        }}
      >
        {busy ? 'Deleting…' : 'Delete this user'}
      </button>
    </div>
  );
}
